import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import Pagination from '../../components/common/Pagination';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import StatusBadge from '../../components/common/StatusBadge';
import ToastStack, { useToasts } from '../../components/common/Toast';
import { useAuth } from '../../context/AuthContext';
import { getTableOrderList, updateTableOrderStatus, settleTableOrderPayment } from '../../api/tableOrder';
import { getStoreList } from '../../api/store';
import { formatDateTime } from '../../utils/format';

const STATUS_OPTIONS = [
  { value: '', label: '전체 상태' },
  { value: 'RECEIVED', label: '접수' },
  { value: 'COOKING', label: '조리중' },
  { value: 'SERVED', label: '서빙완료' },
  { value: 'PAID', label: '결제완료' },
  { value: 'CANCELLED', label: '취소' },
];

// 수령방식 - 테이블 없이 카운터에서 포장 픽업하는 주문 구분
const ORDER_TYPE_OPTIONS = [
  { value: '', label: '전체 수령방식' },
  { value: 'DINE_IN', label: '매장이용' },
  { value: 'TAKEOUT', label: '포장' },
];
const ORDER_TYPE_LABEL = { DINE_IN: '매장이용', TAKEOUT: '포장' };

// 결제상태 - 후결제(POSTPAID)
// 매장의 주문만 결제대기(PENDING)로 생성되고, 나머지(선결제/주문전용)는 항상 결제완료(PAID)로 기록된다.
const PAYMENT_STATUS_OPTIONS = [
  { value: '', label: '전체 결제상태' },
  { value: 'PAID', label: '결제완료' },
  { value: 'PENDING', label: '결제대기' },
];

export default function TableOrderManagePage() {
  const { auth } = useAuth();
  const [stores, setStores] = useState([]);
  const [condition, setCondition] = useState({ pageNo: 1, pageSize: 15, storeId: '', orderStatus: '', orderType: '', paymentStatus: '' });
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusTarget, setStatusTarget] = useState(null);
  const [settlingId, setSettlingId] = useState(null); // 정산완료 처리 중인 주문 (연타 방지)
  const { toasts, pushToast, removeToast } = useToasts();

  useEffect(() => {
    getStoreList().then((res) => setStores(res.data || [])).catch(() => setStores([]));
  }, []);

  const fetchList = useCallback(async (cond, silent = false) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const res = await getTableOrderList(cond);
      setPage(res.data);
    } catch (err) {
      if (!silent) setError(err.response?.data?.message || '테이블오더 목록을 불러오지 못했습니다.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(condition); }, [condition, fetchList]);

  // 매장 카운터/주방 화면에 계속 띄워두는 경우가 많아, 수동 새로고침 없이도
  // 목록이 주기적으로 최신 상태를 유지하도록 가볍게 폴링한다 (10초 간격, 조용히 - 로딩 표시 없이).
  useEffect(() => {
    const interval = setInterval(() => fetchList(condition, true), 10000);
    return () => clearInterval(interval);
  }, [condition, fetchList]);

  const handleConfirmStatusChange = async () => {
    const { order, newStatus } = statusTarget;
    try {
      await updateTableOrderStatus(order.tableOrderId, newStatus, auth.adminName);
      setStatusTarget(null);
      fetchList(condition);
    } catch (err) {
      pushToast(err.response?.data?.message || '상태 변경에 실패했습니다.', 'error');
    }
  };

  /** 후결제(POSTPAID) 매장 주문을 정산완료 처리 - 결제상태만 PAID로 바뀌고 주문상태는 그대로 유지된다. */
  const handleSettlePayment = async (order) => {
    setSettlingId(order.tableOrderId);
    try {
      await settleTableOrderPayment(order.tableOrderId, auth.adminName);
      fetchList(condition);
    } catch (err) {
      pushToast(err.response?.data?.message || '정산완료 처리에 실패했습니다.', 'error');
    } finally {
      setSettlingId(null);
    }
  };

  return (
    <AdminLayout title="테이블오더관리" menuUrl="/admin/table-order">
      <ToastStack toasts={toasts} onDismiss={removeToast} />

      <div className="page-header">
        <div>
          <h1>테이블오더관리</h1>
          <div className="subtitle">매장 테이블에서 접수된 주문을 확인하고 상태를 변경합니다.</div>
        </div>
      </div>

      <div className="list-toolbar">
        <div className="search-bar">
          <select className="select" value={condition.storeId} onChange={(e) => setCondition((c) => ({ ...c, storeId: e.target.value, pageNo: 1 }))}>
            <option value="">전체 매장</option>
            {stores.map((s) => <option key={s.storeId} value={s.storeId}>{s.storeName}</option>)}
          </select>
          <select className="select" value={condition.orderStatus} onChange={(e) => setCondition((c) => ({ ...c, orderStatus: e.target.value, pageNo: 1 }))}>
            {STATUS_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
          <select className="select" value={condition.orderType} onChange={(e) => setCondition((c) => ({ ...c, orderType: e.target.value, pageNo: 1 }))}>
            {ORDER_TYPE_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
          <select className="select" value={condition.paymentStatus} onChange={(e) => setCondition((c) => ({ ...c, paymentStatus: e.target.value, pageNo: 1 }))}>
            {PAYMENT_STATUS_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
          <button className="btn btn-secondary" onClick={() => fetchList(condition)}>새로고침</button>
        </div>
      </div>

      <div className="list-count-bar">
        <span className="list-count">전체 <b>{page?.totalCount ?? 0}</b>건</span>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>주문번호</th><th>매장</th><th>테이블</th><th>수령방식</th><th>금액</th><th>결제상태</th><th>요청사항</th><th>상태</th><th>접수일시</th><th>관리</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={10} className="empty-row">불러오는 중...</td></tr>}
              {!loading && error && <tr><td colSpan={10} className="empty-row error-state">{error}</td></tr>}
              {!loading && !error && page?.list?.length === 0 && <tr><td colSpan={10} className="empty-row">접수된 테이블오더가 없습니다.</td></tr>}
              {!loading && !error && page?.list?.map((o) => (
                <tr key={o.tableOrderId}>
                  <td>{o.tableOrderId}</td>
                  <td>{o.storeName}</td>
                  <td>{o.tableNo}</td>
                  <td>
                    {o.orderType === 'TAKEOUT'
                      ? <span className="badge badge-yellow">포장</span>
                      : <span className="text-tertiary">매장이용</span>}
                  </td>
                  <td>{Number(o.totalAmount).toLocaleString()}원</td>
                  <td>
                    {o.paymentStatus === 'PENDING'
                      ? <span className="badge badge-yellow">결제대기</span>
                      : <span className="text-tertiary">결제완료</span>}
                  </td>
                  <td>
                    {o.memo
                      ? <span className="to-memo-callout" title={o.memo}>{o.memo}</span>
                      : <span className="text-tertiary">-</span>}
                  </td>
                  <td><StatusBadge value={o.orderStatus} label={STATUS_OPTIONS.find((s) => s.value === o.orderStatus)?.label} /></td>
                  <td>{formatDateTime(o.createdAt)}</td>
                  <td className="actions">
                    <select
                      className="select"
                      value=""
                      onChange={(e) => {
                        if (e.target.value) setStatusTarget({ order: o, newStatus: e.target.value });
                        e.target.value = '';
                      }}
                    >
                      <option value="">상태변경</option>
                      {STATUS_OPTIONS.filter((s) => s.value && s.value !== o.orderStatus).map((s) => (
                        <option key={s.value} value={s.value}>{s.label}로 변경</option>
                      ))}
                    </select>
                    {o.paymentStatus === 'PENDING' && (
                      <button
                        className="btn btn-secondary btn-sm"
                        disabled={settlingId === o.tableOrderId}
                        onClick={() => handleSettlePayment(o)}
                      >
                        {settlingId === o.tableOrderId ? '처리 중...' : '정산완료'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {page && <Pagination {...page} onChange={(pageNo) => setCondition((c) => ({ ...c, pageNo }))} />}

      {statusTarget && (
        <ConfirmDialog
          title="주문 상태 변경"
          message={`주문번호 ${statusTarget.order.tableOrderId} 를 '${STATUS_OPTIONS.find((s) => s.value === statusTarget.newStatus)?.label}' 상태로 변경하시겠습니까?${statusTarget.newStatus === 'CANCELLED' ? ' (재고가 즉시 복원되고 테이블이 공석으로 바뀝니다)' : ''}`}
          confirmLabel="변경"
          onCancel={() => setStatusTarget(null)}
          onConfirm={handleConfirmStatusChange}
        />
      )}
    </AdminLayout>
  );
}
